import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/Radio/Radio.module.scss.

const parts: readonly AnatomyPart[] = [
    {
        id: "halo",
        label: "Hover halo",
        element:
            ".control::before, a 28x28 box behind the circle, via --radio-halo",
    },
    {
        id: "border",
        label: "Circle border",
        element: "1px border on the 16px .control, via --radio-border",
    },
    {
        id: "fill",
        label: "Circle fill",
        element: "background on .control, via --radio-fill",
    },
    {
        id: "dot",
        label: "Centre dot",
        element:
            ".control::after, a 6px dot revealed when checked, via --radio-dot",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "outline on .control, from the shared focus-ring mixin",
    },
    {
        id: "label",
        label: "Label",
        element: "color and text style on .label",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector: ":not([data-disabled]):hover",
    },
    {
        id: "pressed",
        label: "Pressed",
        selector: ":not([data-disabled]):active",
    },
    {
        id: "focus",
        label: "Focus",
        selector: ":not([data-disabled]):focus-visible",
    },
    { id: "disabled", label: "Disabled", selector: "[data-disabled]" },
];

const fill = (token: `--${string}`): AnatomyBinding => ({
    partId: "fill",
    property: "background",
    token,
    via: "--radio-fill",
});

const edge = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--radio-border",
});

const halo = (token: `--${string}`): AnatomyBinding => ({
    partId: "halo",
    property: "background",
    token,
    via: "--radio-halo",
});

const dot = (token: `--${string}`): AnatomyBinding => ({
    partId: "dot",
    property: "background",
    token,
    via: "--radio-dot",
});

const labelColor = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
});

const transparentHalo: AnatomyBinding = {
    partId: "halo",
    property: "background",
    literal: "transparent",
    via: "--radio-halo",
    note: "The halo box is always in the DOM; only its fill appears on hover, press and focus.",
};

// The shared mixin draws this ring from Sass variables of the same values the
// --focus-ring-color / --border-width-thick tokens carry (styles/_focus-ring.scss).
const focusRingBindings: readonly AnatomyBinding[] = [
    {
        partId: "focusRing",
        property: "outline-width",
        literal: "2px",
        note: "focus-ring.ring-outline uses Sass $width, not --border-width-thick.",
    },
    {
        partId: "focusRing",
        property: "outline-color",
        token: "--focus-ring-color",
        note: "focus-ring.ring-outline uses Sass $blue-100, the same value this token carries.",
    },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "halo", property: "border-radius", token: "--radius-m" },
    {
        partId: "halo",
        property: "inset",
        literal: "-6px",
        note: "Figma geometry: a 28x28 halo and hit area overflowing the 16px layout box.",
    },
    { partId: "border", property: "width", literal: "16px" },
    { partId: "border", property: "border-width", literal: "1px" },
    { partId: "border", property: "border-radius", literal: "50%" },
    { partId: "dot", property: "width", literal: "6px" },
    {
        partId: "label",
        property: "column-gap",
        token: "--space-8",
        note: "Declared on the .radio row, spacing the control from its label.",
    },
    ...anatomyTextStyle({
        scale: "s",
        weight: "400",
        mixin: "text-s-14-regular",
    }),
];

const uncheckedRestingBindings: readonly AnatomyBinding[] = [
    fill("--fg-on-solid"),
    edge("--border-default"),
    transparentHalo,
    {
        partId: "dot",
        property: "display",
        literal: "none",
        note: "The dot is revealed only by the [data-checked] block.",
    },
    labelColor("--fg-primary"),
];

const checkedRestingBindings: readonly AnatomyBinding[] = [
    fill("--bg-accent"),
    edge("--bg-accent"),
    transparentHalo,
    { partId: "dot", property: "display", literal: "block" },
    dot("--fg-on-solid"),
    labelColor("--fg-primary"),
];

const variants: readonly AnatomyVariant[] = [
    {
        id: "unchecked",
        label: "Unchecked",
        selector: "",
        restingBindings: uncheckedRestingBindings,
        stateBindings: {
            hover: [edge("--border-hover"), halo("--bg-neutral-subtle-hover")],
            pressed: [
                fill("--bg-secondary"),
                edge("--border-hover"),
                halo("--bg-neutral-subtle-hover"),
            ],
            focus: [
                edge("--border-accent-focus"),
                halo("--bg-neutral-subtle-hover"),
                ...focusRingBindings,
            ],
            disabled: [edge("--border-disabled"), labelColor("--fg-disabled")],
        },
    },
    {
        id: "checked",
        label: "Checked",
        selector: "[data-checked]",
        restingBindings: checkedRestingBindings,
        stateBindings: {
            hover: [
                fill("--bg-accent-hover"),
                edge("--bg-accent-hover"),
                halo("--bg-neutral-subtle-hover"),
            ],
            pressed: [
                fill("--bg-accent-pressed"),
                edge("--bg-accent-pressed"),
                halo("--bg-neutral-subtle-hover"),
            ],
            focus: [
                {
                    ...fill("--bg-accent-hover"),
                    note: "Focus lifts the fill to the hover colour. A checked checkbox on focus keeps --bg-accent instead — the two components diverge here.",
                },
                edge("--border-accent-focus"),
                halo("--bg-neutral-subtle-hover"),
                ...focusRingBindings,
            ],
            disabled: [
                fill("--bg-disabled"),
                edge("--bg-disabled"),
                dot("--fg-tertiary"),
                labelColor("--fg-disabled"),
            ],
        },
    },
];

export const radioAnatomy: ComponentAnatomySpec = {
    label: "Radio",
    schematic: "radio",
    rootSelector: ".radio",
    variantAxisLabel: "Value",
    parts,
    states,
    sharedBindings,
    variants,
};
